import { ChangeEvent, FC, FocusEvent, useMemo, useRef, useState } from "react";

import TextInput from "components/TextInput/TextInput";
import { ValueOrArray } from "config/types";

import { DateTime } from "luxon";

interface IProps {
  id: string;
  dataTestId?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: ValueOrArray<string>;
  label?: string;
  placeholder?: string;
  shouldDisplayUtcAsLocal?: boolean;
  value?: string; // iso string
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
  onChange?(event: ChangeEvent<HTMLInputElement>): void;
  onFocus?(event: FocusEvent<HTMLInputElement>): void;
}

const DateInput: FC<IProps> = props => {
  const {
    dataTestId,
    disabled = false,
    error,
    errorMessage,
    id,
    label,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    shouldDisplayUtcAsLocal,
    value,
  } = props;

  const formattedDate = useMemo(() => {
    if (!value) {
      return "";
    }

    let dateTime;
    if (shouldDisplayUtcAsLocal && (value.endsWith("+00:00") || value.endsWith("Z"))) {
      dateTime = DateTime.fromISO(value, { zone: "Etc/UTC" });
    } else {
      dateTime = DateTime.fromISO(value);
    }

    return dateTime.isValid ? dateTime.toFormat("MM/dd/yyyy") : value;
  }, [value, shouldDisplayUtcAsLocal]);

  // Non-null only while user is actively editing; null = show formattedDate
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const displayValue = editingValue ?? formattedDate;
  const inputRef = useRef<HTMLInputElement>(null);

  const getCursorPosition = (digitCount: number) => {
    if (digitCount <= 2) {
      return digitCount;
    }
    if (digitCount <= 4) {
      return digitCount + 1;
    }
    return digitCount + 2;
  };

  const setCursorAfterDigits = (digitCount: number) => {
    const cursorPos = getCursorPosition(digitCount);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = cursorPos;
        inputRef.current.selectionEnd = cursorPos;
      }
    });
  };

  const formatWithMask = (raw: string) => {
    const padded = raw + "________".slice(raw.length);
    return padded.slice(0, 2) + "/" + padded.slice(2, 4) + "/" + padded.slice(4, 8);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Strip non-digits and underscores, cap at 8
    const raw = event.target.value.replace(/[^\d]/g, "").slice(0, 8);
    setEditingValue(formatWithMask(raw));
    setCursorAfterDigits(raw.length);

    // Convert complete dates to ISO for the form, pass partial values as-is
    const formatted = formatWithMask(raw);
    const dateTime = DateTime.fromFormat(formatted, "MM/dd/yyyy");
    const isoValue = dateTime.isValid ? (dateTime.toISO() ?? formatted) : formatted;

    onChange?.({
      target: { id, value: isoValue, files: null },
    } as ChangeEvent<HTMLInputElement>);
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    setEditingValue(null);
    onBlur && onBlur(event);
  };

  const handleOnFocus = (event: FocusEvent<HTMLInputElement>) => {
    const currentRaw = formattedDate.replace(/[^\d]/g, "");
    setEditingValue(formatWithMask(currentRaw));
    setCursorAfterDigits(currentRaw.length);
    onFocus && onFocus(event);
  };

  const labelText = placeholder || label;

  return (
    <TextInput
      type="text"
      label={labelText ? labelText : "mm/dd/yyyy"}
      id={id}
      error={error}
      errorMessage={errorMessage}
      dataTestId={dataTestId || "text-input__date"}
      inputMode="numeric"
      disabled={disabled}
      onBlur={handleOnBlur}
      onChange={handleInputChange}
      onFocus={handleOnFocus}
      inputRef={inputRef}
      value={displayValue}
    />
  );
};

export default DateInput;
